import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ConfiguratorDatastoreService } from '@tj/tj-dmp-lib-api';
import { SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'dmp-select-partner',
    templateUrl: './select-partner.component.html',
    styleUrls: ['./select-partner.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: SelectPartnerComponent,
        },
    ],
    standalone: false
})
export class SelectPartnerComponent implements OnChanges, ControlValueAccessor {
    partner: string | string[];
    partners: SelectItem[] = [];
    @Input() inputId = '';
    @Input() fluid = false;
    @Input() multiSelect = false;
    @Input() value: string | string[];
    @Input() datastore: string;
    @Input() disabled = false;
    @Input() emptyValue = true;
    @Input() accessiblePartners = false;
    @Input() affectedPartners: string[];
    @Input() initializeFromStorage = true;
    /** Codes toujours sélectionnés et non désélectionnables (multiSelect uniquement) — ex. partenaire par défaut du tenant */
    @Input() pinnedValues: string[] = [];
    @Output() change = new EventEmitter<string | string[]>();
    touched = false;

    constructor(
        protected datastoreResourceService: ConfiguratorDatastoreService,
        protected translateService: TranslateService,
        private cref: ChangeDetectorRef
    ) { }

    onChange = (value) => {
        //
    }
    onTouched = () => {
        //
    }

    writeValue(codePartner: any): void {
        // this.value = codePartner;

        if (this.multiSelect) {
            this.value = Array.isArray(codePartner) ? codePartner : (codePartner ? [codePartner] : []);
        } else {
            this.value = Array.isArray(codePartner) && codePartner.length > 0 ? codePartner[0] : codePartner;
        }

        this.updateValue();
        this.cref.detectChanges();
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    ngOnInit(): void {
        if (!this.initializeFromStorage) {
            return;
        }
        const actual = localStorage.getItem('group');
        if (actual) {
            this.partner = actual.split(':')[1];
            this.change.emit(this.partner);
            this.updateValue()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value || changes.pinnedValues) {
            this.updateValue();
        }
        if (changes.datastore && this.datastore == null) {
            this.partners = [];
        } else if (changes.datastore && this.datastore) {
            this.datastoreResourceService
                .getDatastoreUsingGET(this.datastore, true)
                .subscribe((datastore) => {
                    this.partners = [];
                    if (this.emptyValue && !this.multiSelect) {
                        this.partners.push({
                            label: /*this.translateService.instant("home.partner")+*/ '...',
                            value: null,
                        });
                    }
                    if (this.accessiblePartners) {
                        this.datastoreResourceService
                            .getDatastoreAccessiblePartnersUsingGET(this.datastore, true)
                            .subscribe((accessiblePartners) => {
                                const partners = datastore.partners.filter((p) => {
                                    return (
                                        accessiblePartners.find((ap) => ap === p.code) !== undefined
                                    );
                                });
                                if (partners) {
                                    partners.forEach((p) => {
                                        //
                                        const value = {
                                            label: `${p.name} [${p.code}]`,
                                            value: p.code,
                                            disabled: this.multiSelect && this.pinnedValues.includes(p.code),
                                        };
                                        //
                                        this.partners.push(value);
                                    });
                                    if (this.affectedPartners && this.affectedPartners.length > 0) {
                                        this.partners = this.partners.filter(p => this.affectedPartners.includes(p.value as string));
                                    }
                                    this.updateValue();
                                }
                            });
                    } else if (datastore.partners) {
                        datastore.partners.forEach((p) => {
                            //
                            const value = {
                                label: `${p.name} [${p.code}]`,
                                value: p.code,
                                disabled: this.multiSelect && this.pinnedValues.includes(p.code),
                            };
                            //
                            this.partners.push(value);
                        });
                        if (this.affectedPartners && this.affectedPartners.length > 0) {
                            this.partners = this.partners.filter(p => this.affectedPartners.includes(p.value as string));
                        }
                        this.updateValue();
                    }
                });
        }
    }

    partnerChange(event = null) {
        if (!this.disabled) {
            this.onChange((this.partner));
            this.markAsTouched();
            this.change.emit(this.partner);
        }
    }

    partnerChangeMultiSelect(event = null) {
        if (!this.disabled) {
            this.partner = event ? event.value : this.partner;
            this.onChange(this.partner);
            this.markAsTouched();
            this.change.emit(this.partner);
        }
    }

    private updateValue() {
        // if (!this.partners || this.partners.length === 0) {
        //     return;
        // }

        if (this.value && this.partners) {
            if (this.multiSelect && Array.isArray(this.value)) {
                this.partner = this.value.filter(val => this.partners.some(p => p.value === val));
            } else if (!this.multiSelect) {
                this.partner = this.partners.find((d) => d.value === this.value)?.value;
            }

        } else if (!this.value && this.partners?.length > 0 && !this.multiSelect) {
            this.partner = this.partners[0]?.value;
        }

        // Les valeurs "épinglées" (ex. partenaire par défaut du tenant) restent toujours sélectionnées, même si
        // absentes de la valeur initiale/saisie (nouveau job, ou valeur existante créée avant cette règle).
        if (this.multiSelect && this.pinnedValues?.length > 0 && this.partners) {
            // pinnedValues peut arriver après la construction de `partners` (fetch parent séparé) : (ré)appliquer
            // le flag disabled ici aussi, pas seulement à la construction de la liste.
            this.partners.forEach(p => {
                if (this.pinnedValues.includes(p.value as string)) {
                    p.disabled = true;
                }
            });
            const current = Array.isArray(this.partner) ? this.partner : [];
            const validPinned = this.pinnedValues.filter(pv => this.partners.some(p => p.value === pv));
            const merged = Array.from(new Set([...current, ...validPinned]));
            const wasMissingPinned = validPinned.some(pv => !current.includes(pv));
            this.partner = merged;
            // Propage au form parent (ControlValueAccessor) si un partenaire épinglé a dû être ajouté : sans ça,
            // le form ne contiendrait le partenaire par défaut que si l'utilisateur interagit avec ce composant.
            if (wasMissingPinned) {
                this.onChange(this.partner);
            }
        }

        // if (this.partner !== undefined) {
        //     this.partnerChange();
        // }
    }
}
